import { useState, forwardRef } from "react";
import { useSelector } from "react-redux";
import { Button } from "../Button";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import Modal from "../Modal";
import { shareFunctionsMap } from "@helpers/share";
import { ReactComponent as IconShareGrey } from "@assets/ic-share.svg";
import useShareContent from "./useShareContent";

import styles from "./index.module.scss";
import ShareFooter from "../ShareFooter";

/**
 * Share modal w/ button
 * @param {Object} props
 * @param {string} props.message
 * @param {boolean} props.resetStyle
 * @param {string} props.togglerClassName
 * @param {string} props.uniqueKey
 * @param {string} props.username
 * @param {string} props.postUniqueKey
 * @param {string} props.subscriptionUniqueKey
 * @param {string} props.streamUniqueKey
 * @param {boolean} props.withBody
 * @param {React.ReactNode} props.toggler
 * @param {"post" | "subscription" | "profile" | "stream" | "contentRequest" } props.type
 */
const Share = forwardRef(
  (
    {
      message = "",
      type = "post",
      resetStyle,
      togglerClassName,
      uniqueKey,
      postUniqueKey,
      subscriptionUniqueKey,
      toggler,
      iconClassName,
      withBody,
      isMyProfile,
      modalTitleVariables,
      username,
      color,
      size,
      streamUniqueKey,
      propsTitleKey,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation("modals");

    const { newFanReferralCode } = useSelector(
      ({ profileReducer }) => profileReducer,
    );

    const shareLinkParams = {
      username,
      postUniqueKey,
      subscriptionUniqueKey,
      referralCode: newFanReferralCode,
      isMyProfile,
      uniqueKey,
      streamUniqueKey,
    };

    const shareFunction = shareFunctionsMap[type];

    const toggle = (e) => {
      if (e && typeof e?.stopPropagation === "function") e.stopPropagation();
      setIsOpen((state) => !state);
    };

    const { body, titleKey, titleParams } = useShareContent({
      isMyProfile,
      withBody,
      type,
      username,
    });
    const shareLink = shareFunction(shareLinkParams);

    return (
      <>
        <Button
          innerRef={ref}
          onClick={toggle}
          color={color}
          size={size}
          className={classNames("d-flex", togglerClassName)}
          type="button"
        >
          {toggler || (
            <IconShareGrey className={classNames(styles.icon, iconClassName)} />
          )}
        </Button>
        <Modal
          centered
          onClose={toggle}
          toggle={toggle}
          dialogClassName={classNames(styles.dialog)}
          bodyClassName={styles.dialogBody}
          titleClassName={styles.dialogTitle}
          footerClassName="d-none"
          title={t(propsTitleKey ?? titleKey, {
            ...modalTitleVariables,
            ...titleParams,
          })}
          isOpen={isOpen}
        >
          <div className={classNames(!withBody && "d-none")}>{body}</div>
          <ShareFooter
            type={type}
            postUniqueKey={postUniqueKey}
            subscriptionUniqueKey={subscriptionUniqueKey}
            message={message}
            shareLink={shareLink}
            withBody={withBody}
            withQrCode={type === "profile"}
            username={username}
            streamUniqueKey={streamUniqueKey}
          />
        </Modal>
      </>
    );
  },
);

Share.displayName = "Share";

export default Share;
